import React, { useState, useEffect, useMemo, useRef } from 'react';
import { format, isToday, isFuture, differenceInDays } from 'date-fns';
import { el } from 'date-fns/locale';
import { clientApi, bookingApi } from '../services/api';
import { useAppLayout } from '../components/AppLayout';
import './ClientsPage.css';

const AVATAR_COLORS = [
  '#534AB7','#E74C3C','#2ECC71','#F39C12',
  '#3498DB','#9B59B6','#E91E63','#00BCD4',
  '#FF5722','#607D8B',
];

const avatarColor = (name = '') => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const initials = (name = '') =>
  name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

// ── ClientModal ───────────────────────────────────────────────
function ClientModal({ client, onSave, onClose }) {
  const isEdit = !!client;
  const [form, setForm] = useState({
    name:  client?.name  || '',
    phone: client?.phone || '',
    email: client?.email || '',
    notes: client?.notes || '',
  });
  const [touched, setTouched] = useState({});
  const [saving, setSaving]   = useState(false);
  const [serverError, setServerError] = useState('');

  const validate = (f) => {
    const errs = {};
    if (!f.name.trim())
      errs.name = '* Το όνομα είναι υποχρεωτικό';
    if (f.phone && !/^[0-9\s\+\-\(\)]{7,15}$/.test(f.phone.trim()))
      errs.phone = '* Μη έγκυρος αριθμός τηλεφώνου';
    if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim()))
      errs.email = '* Μη έγκυρη μορφή email (π.χ. name@example.com)';
    return errs;
  };

  const errors  = validate(form);
  const hasErrs = Object.keys(errors).length > 0;

  const blur  = (field) => setTouched(t => ({ ...t, [field]: true }));
  const touch = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, phone: true, email: true });
    if (hasErrs) return;
    setSaving(true); setServerError('');
    try {
      await onSave({ ...form, id: client?.id });
    } catch (err) {
      const data = err.response?.data;
      setServerError(data?.errors?.[0]?.msg || data?.error || 'Σφάλμα αποθήκευσης. Δοκίμασε ξανά.');
    } finally { setSaving(false); }
  };

  return (
    <div className="cp-overlay">
      <div className="cp-modal">
        <div className="cp-modal-header">
          <span className="cp-modal-title">{isEdit ? '✏️ Επεξεργασία πελάτη' : '➕ Νέος πελάτης'}</span>
          <button className="cp-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit} className="cp-modal-body">
          <div className={`cp-field ${touched.name && errors.name ? 'cp-field-error' : ''}`}>
            <label>Όνομα <span className="cp-required">*</span></label>
            <input type="text" placeholder="π.χ. Μαρία Παπαδοπούλου"
              value={form.name} onChange={e => touch('name', e.target.value)}
              onBlur={() => blur('name')} autoFocus />
            {touched.name && errors.name && <span className="cp-field-msg">{errors.name}</span>}
          </div>

          <div className={`cp-field ${touched.phone && errors.phone ? 'cp-field-error' : ''}`}>
            <label>Τηλέφωνο</label>
            <input type="tel" placeholder="69ΧΧΧΧΧΧΧΧ"
              value={form.phone} onChange={e => touch('phone', e.target.value)}
              onBlur={() => blur('phone')} />
            {touched.phone && errors.phone && <span className="cp-field-msg">{errors.phone}</span>}
          </div>

          <div className={`cp-field ${touched.email && errors.email ? 'cp-field-error' : ''}`}>
            <label>Email</label>
            <input type="text" placeholder="email@example.com"
              value={form.email} onChange={e => touch('email', e.target.value)}
              onBlur={() => blur('email')} />
            {touched.email && errors.email && <span className="cp-field-msg">{errors.email}</span>}
          </div>

          <div className="cp-field">
            <label>Σημειώσεις</label>
            <textarea placeholder="Προτιμήσεις, αλλεργίες..."
              value={form.notes} onChange={e => touch('notes', e.target.value)} rows={3} />
          </div>

          {serverError && <div className="cp-error">{serverError}</div>}

          <div className="cp-modal-footer">
            <button type="button" className="cp-btn cp-btn-sec" onClick={onClose}>Ακύρωση</button>
            <button type="submit" className="cp-btn cp-btn-pri" disabled={saving}>
              {saving ? 'Αποθήκευση...' : isEdit ? '💾 Αποθήκευση' : '➕ Προσθήκη'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── ClientDrawer ──────────────────────────────────────────────
function ClientDrawer({ client, onEdit, onClose }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(true);
  const color = avatarColor(client.name);

  useEffect(() => {
    const from = new Date(Date.now() - 365 * 86400000).toISOString(); // 1 χρόνο πίσω
    const to   = new Date(Date.now() + 60 * 86400000).toISOString();
    bookingApi.getAll(from, to)
      .then(r => {
        const mine = (r.data.bookings || []).filter(b =>
          b.client_phone === client.phone || b.client_name === client.name
        );
        mine.sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));
        setBookings(mine);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Στατιστικά ────────────────────────────────────────────
  const stats = useMemo(() => {
    const active = bookings.filter(b => b.status !== 'cancelled');
    const completed = bookings.filter(b => b.status === 'completed' || (!isFuture(new Date(b.starts_at)) && b.status !== 'cancelled'));
    const upcoming  = bookings.filter(b => isFuture(new Date(b.starts_at)) && b.status !== 'cancelled');

    // Έσοδα + φιλοδωρήματα
    const revenue = active.reduce((sum, b) => {
      const price = b.service?.price || b.service_price || 0;
      return sum + parseFloat(price || 0);
    }, 0);
    const totalTips = active.reduce((sum, b) => sum + (parseFloat(b.tip) || 0), 0);
    const totalRevenue = revenue + totalTips;

    // Αγαπημένη υπηρεσία
    const svcCount = {};
    active.forEach(b => {
      const s = b.service_name || '—';
      svcCount[s] = (svcCount[s] || 0) + 1;
    });
    const topService = Object.entries(svcCount).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    // Αγαπημένος υπάλληλος
    const staffCount = {};
    active.forEach(b => {
      if (b.staff?.name) staffCount[b.staff.name] = (staffCount[b.staff.name] || 0) + 1;
    });
    const topStaff = Object.entries(staffCount).sort((a, b) => b[1] - a[1])[0]?.[0] || null;

    // Μέση συχνότητα (μέρες μεταξύ επισκέψεων)
    const dates = completed.map(b => new Date(b.starts_at)).sort((a, b) => a - b);
    let avgFreq = null;
    if (dates.length >= 2) {
      const gaps = dates.slice(1).map((d, i) => differenceInDays(d, dates[i]));
      avgFreq = Math.round(gaps.reduce((s, g) => s + g, 0) / gaps.length);
    }

    // Τελευταία επίσκεψη
    const lastVisit = completed.filter(b => !isFuture(new Date(b.starts_at)))[0];

    return { total: active.length, upcoming: upcoming.length, completed: completed.length, revenue: totalRevenue, totalTips, topService, topStaff, avgFreq, lastVisit };
  }, [bookings]);

  return (
    <div className="cp-drawer-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cp-drawer">
        {/* Header */}
        <div className="cp-drawer-header" style={{ '--dcolor': color }}>
          <button className="cp-drawer-close" onClick={onClose}>×</button>
          <div className="cp-drawer-avatar" style={{ background: color }}>{initials(client.name)}</div>
          <div className="cp-drawer-name">{client.name}</div>
          {client.phone && <div className="cp-drawer-phone">📞 {client.phone}</div>}
          {client.email && <div className="cp-drawer-email">✉️ {client.email}</div>}
          <button className="cp-drawer-edit-btn" onClick={() => onEdit(client)}>✏️ Επεξεργασία</button>
        </div>

        {/* Stats grid */}
        <div className="cp-drawer-stats">
          <div className="cp-dstat">
            <span className="cp-dstat-val">{stats.total}</span>
            <span className="cp-dstat-lbl">Συνολικές</span>
          </div>
          <div className="cp-dstat">
            <span className="cp-dstat-val">{stats.upcoming}</span>
            <span className="cp-dstat-lbl">Προσεχείς</span>
          </div>
          <div className="cp-dstat">
            <span className="cp-dstat-val" style={{ color: 'var(--green, #16a34a)' }}>
              {stats.revenue > 0 ? `€${stats.revenue.toFixed(0)}` : '—'}
            </span>
            <span className="cp-dstat-lbl">Έσοδα</span>
          </div>
          <div className="cp-dstat">
            <span className="cp-dstat-val">{stats.avgFreq ? `${stats.avgFreq}μ` : '—'}</span>
            <span className="cp-dstat-lbl">Συχνότητα</span>
          </div>
        </div>

        {/* Insights row */}
        {(stats.topService || stats.topStaff || stats.lastVisit) && (
          <div className="cp-drawer-insights">
            {stats.topService && (
              <div className="cp-insight">
                <span className="cp-insight-icon">✂️</span>
                <div>
                  <div className="cp-insight-label">Αγαπημένη υπηρεσία</div>
                  <div className="cp-insight-val">{stats.topService}</div>
                </div>
              </div>
            )}
            {stats.topStaff && (
              <div className="cp-insight">
                <span className="cp-insight-icon">💇</span>
                <div>
                  <div className="cp-insight-label">Προτιμά υπάλληλο</div>
                  <div className="cp-insight-val">{stats.topStaff}</div>
                </div>
              </div>
            )}
            {stats.lastVisit && (
              <div className="cp-insight">
                <span className="cp-insight-icon">🗓️</span>
                <div>
                  <div className="cp-insight-label">Τελευταία επίσκεψη</div>
                  <div className="cp-insight-val">
                    {isToday(new Date(stats.lastVisit.starts_at))
                      ? 'Σήμερα'
                      : format(new Date(stats.lastVisit.starts_at), 'd MMM yyyy', { locale: el })}
                  </div>
                </div>
              </div>
            )}
            {stats.totalTips > 0 && (
              <div className="cp-insight">
                <span className="cp-insight-icon">💰</span>
                <div>
                  <div className="cp-insight-label">Φιλοδωρήματα</div>
                  <div className="cp-insight-val" style={{ color: 'var(--green, #16a34a)' }}>
                    €{stats.totalTips.toFixed(2)}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes */}
        {client.notes && (
          <div className="cp-drawer-notes">
            <div className="cp-drawer-section-title">📝 Σημειώσεις</div>
            <p>{client.notes}</p>
          </div>
        )}

        {/* History */}
        <div className="cp-drawer-history">
          <div className="cp-drawer-section-title">🗓️ Ιστορικό κρατήσεων</div>
          {loading && <div className="cp-drawer-loading">Φόρτωση...</div>}
          {!loading && bookings.length === 0 && (
            <div className="cp-drawer-empty">Δεν υπάρχουν κρατήσεις ακόμα</div>
          )}
          {!loading && bookings.map(b => {
            const d = new Date(b.starts_at);
            const isCancelled = b.status === 'cancelled';
            const price = b.service?.price || b.service_price;
            return (
              <div key={b.id} className={`cp-hist-item ${isCancelled ? 'cancelled' : ''}`}>
                <div className="cp-hist-dot" style={{ background: isCancelled ? '#ccc' : (b.staff?.color || '#534AB7') }} />
                <div className="cp-hist-body">
                  <div className="cp-hist-service">
                    {b.service_name || '—'}
                    {price && !isCancelled && (
                      <span className="cp-hist-price"> · €{parseFloat(price).toFixed(0)}</span>
                    )}
                    {b.tip && parseFloat(b.tip) > 0 && !isCancelled && (
                      <span className="cp-hist-tip"> +€{parseFloat(b.tip).toFixed(0)} 💰</span>
                    )}
                  </div>
                  <div className="cp-hist-meta">
                    {isToday(d) ? 'Σήμερα' : format(d, 'EEE d MMM yyyy', { locale: el })}
                    {' · '}{format(d, 'HH:mm')}
                    {b.staff && <span> · {b.staff.name}</span>}
                  </div>
                </div>
                <span className={`cp-hist-badge cp-hist-${b.status}`}>
                  {{ confirmed:'✓', pending:'…', cancelled:'✕', completed:'✓', no_show:'✗' }[b.status]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main ClientsPage ──────────────────────────────────────────
export default function ClientsPage() {
  const { openSidebar } = useAppLayout();
  const [clients, setClients]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [search, setSearch]             = useState('');
  const [debouncedSearch, setDebounced] = useState('');
  const [showModal, setShowModal]       = useState(false);
  const [editClient, setEditClient]     = useState(null);
  const [activeClient, setActiveClient] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(search), 300);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    clientApi.search(debouncedSearch)
      .then(r => setClients(r.data.clients || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [debouncedSearch]);

  const handleSave = async (data) => {
    if (data.id) {
      const res = await clientApi.update(data.id, data);
      setClients(prev => prev.map(c => c.id === data.id ? res.data.client : c));
      if (activeClient?.id === data.id) setActiveClient(res.data.client);
    } else {
      const res = await clientApi.create(data);
      setClients(prev => [res.data.client, ...prev]);
    }
    setShowModal(false); setEditClient(null);
  };

  const openEdit = (client) => {
    setActiveClient(null);
    setEditClient(client);
    setShowModal(true);
  };

  const sorted = useMemo(() =>
    [...clients].sort((a, b) => a.name.localeCompare(b.name, 'el')),
  [clients]);

  const grouped = useMemo(() => {
    const g = {};
    sorted.forEach(c => {
      const letter = c.name[0]?.toUpperCase() || '#';
      if (!g[letter]) g[letter] = [];
      g[letter].push(c);
    });
    return Object.entries(g).sort(([a], [b]) => a.localeCompare(b, 'el'));
  }, [sorted]);

  return (
    <div className="cp-page">
      {/* ── Header ───────────────────────────────────────────── */}
      <div className="cp-header">
        <div className="cp-header-top">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="hamburger-btn" onClick={openSidebar}>☰</button>
            <h1 className="cp-title">👥 Πελάτες</h1>
          </div>
          <button className="cp-add-btn" onClick={() => { setEditClient(null); setShowModal(true); }}>
            + Νέος πελάτης
          </button>
        </div>

        <div className="cp-search-wrap">
          <span className="cp-search-icon">🔍</span>
          <input className="cp-search" placeholder="Αναζήτηση ονόματος ή τηλεφώνου..."
            value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button className="cp-search-clear" onClick={() => setSearch('')}>×</button>}
        </div>

        <div className="cp-count">
          {loading ? 'Φόρτωση...' : `${clients.length} πελάτ${clients.length === 1 ? 'ης' : 'ες'}`}
        </div>
      </div>

      {/* ── Content ──────────────────────────────────────────── */}
      <div className="cp-content">
        {!loading && clients.length === 0 && (
          <div className="cp-empty">
            <div className="cp-empty-icon">👤</div>
            <p className="cp-empty-title">
              {search ? 'Δεν βρέθηκαν αποτελέσματα' : 'Δεν υπάρχουν πελάτες ακόμα'}
            </p>
            {!search && <p className="cp-empty-sub">Πρόσθεσε τον πρώτο σου πελάτη</p>}
          </div>
        )}

        {grouped.map(([letter, items]) => (
          <div key={letter} className="cp-group">
            <div className="cp-group-letter">{letter}</div>
            <div className="cp-grid">
              {items.map(c => {
                const color = avatarColor(c.name);
                return (
                  <div key={c.id} className="cp-card" onClick={() => setActiveClient(c)}>
                    <div className="cp-card-avatar" style={{ background: color }}>{initials(c.name)}</div>
                    <div className="cp-card-info">
                      <div className="cp-card-name">{c.name}</div>
                      {c.phone && <div className="cp-card-detail">📞 {c.phone}</div>}
                      {c.email && <div className="cp-card-detail">✉️ {c.email}</div>}
                      {c.notes && <div className="cp-card-notes">{c.notes}</div>}
                    </div>
                    <div className="cp-card-arrow">›</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <ClientModal client={editClient} onSave={handleSave}
          onClose={() => { setShowModal(false); setEditClient(null); }} />
      )}

      {activeClient && (
        <ClientDrawer client={activeClient} onEdit={openEdit}
          onClose={() => setActiveClient(null)} />
      )}
    </div>
  );
}